import { cache } from 'react';
import { type ClassValue, clsx } from 'clsx';
import { Room } from 'livekit-client';
import { twMerge } from 'tailwind-merge';
import type { ReceivedChatMessage, TextStreamData } from '@livekit/components-react';
import { APP_CONFIG_DEFAULTS } from '@/app-config';
import type { AppConfig, SandboxConfig } from './types';

export const CONFIG_ENDPOINT = process.env.NEXT_PUBLIC_APP_CONFIG_ENDPOINT;
export const SANDBOX_ID = process.env.SANDBOX_ID;

export const THEME_STORAGE_KEY = 'theme-mode';
export const THEME_MEDIA_QUERY = '(prefers-color-scheme: dark)';

// 这些路由的页面使用硬编码的浅色配色（bg-white / slate 渐变等），未适配暗色模式。
// 若跟随系统/用户的暗色偏好，继承的浅色前景字会让输入框、下拉框中的文字
// 与浅色背景几乎同色而不可见，因此这些路由强制使用浅色配色方案。
export const LIGHT_ONLY_ROUTE_PREFIXES = [
  '/doctor',
  '/hospital-admin',
  '/system-admin',
  '/patient',
  '/sessions',
  '/animation-test',
  '/clear-cache',
  '/clinical-note-print',
];

export function isLightOnlyRoute(pathname: string | null | undefined): boolean {
  if (!pathname) return false;
  return LIGHT_ONLY_ROUTE_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );
}

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// 后端时间以 naive UTC ISO 字符串（datetime.utcnow().isoformat()）存储，不带时区后缀。
// new Date() 会把它当浏览器本地时间解析，导致显示偏移时区差（如 +8 小时），
// 因此统一补 'Z' 后再解析。
export function parseBackendDate(value: string): Date {
  const hasTimezone = /(?:[Zz]|[+-]\d{2}:?\d{2})$/;
  return new Date(hasTimezone.test(value) ? value : `${value}Z`);
}

// 统一按北京时间渲染后端时间字符串（2026-10-08 起后端统一 aware +08:00，
// 少数遗留 naive 值按 UTC 解析后折算），无论设备系统时区如何都显示北京时间。
export function formatBeijingTime(value: string, options?: Intl.DateTimeFormatOptions): string {
  return parseBackendDate(value).toLocaleString('zh-CN', {
    timeZone: 'Asia/Shanghai',
    ...options,
  });
}

export function isAgentIdentity(identity?: string | null): boolean {
  if (!identity) return false;
  const normalized = identity.toLowerCase();
  return (
    normalized === 'agent' ||
    normalized === 'assistant' ||
    normalized.startsWith('agent-') ||
    normalized.startsWith('agent_') ||
    normalized.startsWith('assistant-') ||
    normalized.startsWith('assistant_')
  );
}

export function transcriptionToChatMessage(
  textStream: TextStreamData,
  room: Room
): ReceivedChatMessage {
  return {
    id: textStream.streamInfo.id,
    timestamp: textStream.streamInfo.timestamp,
    message: textStream.text,
    from:
      textStream.participantInfo.identity === room.localParticipant.identity
        ? room.localParticipant
        : Array.from(room.remoteParticipants.values()).find(
            (p) => p.identity === textStream.participantInfo.identity
          ),
  };
}

// https://react.dev/reference/react/cache#caveats
// > React will invalidate the cache for all memoized functions for each server request.
export const getAppConfig = cache(async (headers: Headers): Promise<AppConfig> => {
  if (CONFIG_ENDPOINT) {
    const sandboxId = SANDBOX_ID ?? headers.get('x-sandbox-id') ?? '';

    try {
      if (!sandboxId) {
        throw new Error('Sandbox ID is required');
      }

      const response = await fetch(CONFIG_ENDPOINT, {
        cache: 'no-store',
        headers: { 'X-Sandbox-ID': sandboxId },
      });

      const remoteConfig: SandboxConfig = await response.json();
      const config: AppConfig = { sandboxId, ...APP_CONFIG_DEFAULTS };

      for (const [key, entry] of Object.entries(remoteConfig)) {
        if (entry === null) continue;
        // Only include app config entries that are declared in defaults and, if set,
        // share the same primitive type as the default value.
        if (
          (key in APP_CONFIG_DEFAULTS &&
            APP_CONFIG_DEFAULTS[key as keyof AppConfig] === undefined) ||
          (typeof config[key as keyof AppConfig] === entry.type &&
            typeof config[key as keyof AppConfig] === typeof entry.value)
        ) {
          // @ts-expect-error I'm not sure quite how to appease TypeScript, but we've thoroughly checked types above
          config[key as keyof AppConfig] = entry.value as AppConfig[keyof AppConfig];
        }
      }

      return config;
    } catch (error) {
      console.error('ERROR: getAppConfig() - lib/utils.ts', error);
    }
  }

  return APP_CONFIG_DEFAULTS;
});

/**
 * Convert messages array to plain text transcript for clinical note generation
 * @param messages Array of chat messages from useChatAndTranscription
 * @returns Plain text transcript formatted for LLM processing
 */
export function messagesToTranscript(messages: ReceivedChatMessage[]): string {
  if (!messages || messages.length === 0) {
    return '';
  }

  return messages
    .map((message) => {
      const participant = message.from;
      const role = isAgentIdentity(participant?.identity) ? 'AI Assistant' : 'Patient';
      const content = message.message || '';
      const timestamp = new Date(message.timestamp).toLocaleTimeString();

      return `[${timestamp}] ${role}: ${content}`;
    })
    .join('\n');
}
